import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../component/Header';
import Footer from '../component/Footer';
import { 
    TrendingUp,
    GraduationCap, Briefcase, 
  ArrowRight
} from 'lucide-react';


const steps = [
    {
      id: 1,
      num: '01',
      title: 'Create an account',
      description: 'Quick signup unlocks access to the full course library. Bypass lengthy onboarding and dive straight into the material in under two minutes.',
      image: '/hands-laptop-keyboard.jpg'
    },
    {
      id: 2,
      num: '02',
      title: 'Dive into courses',
      description: 'Choose your level and follow structured, beginner-to-pro tracks. Learn from industry experts through high-definition video lessons and interactive resources.',
      image: '/library.jpg'
    },
    {
      id: 3,
      num: '03',
      title: 'Download anytime',
      description: 'Access your lessons anywhere, even without a Wi-Fi connection. Download materials directly to your device for seamless offline review during your commute.',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1200&q=80'
    }
  ];

const AILearningHome = () => {
    const [activeStep, setActiveStep] = useState(1);
  const actionColorScheme = 'bg-zinc-950 border border-zinc-950 text-white hover:bg-white hover:text-zinc-950 hover:border-zinc-950';

    return (
        <>
            <Header />

            <div className="font-sans text-zinc-900 ">

                {/* ========================================
                    1. HERO SECTION (Business Scaling Focus)
                    ======================================== */}
 <section className="relative py-12 md:py-12 overflow-hidden border-b border-zinc-200">
  <div
    className="absolute inset-0 opacity-20"
    style={{
      backgroundImage:
        "linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)",
      backgroundSize: "40px 40px",
    }}
  ></div>

  <div className="absolute inset-0 "></div>

  <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-[#f59e0b]/10 rounded-full blur-[120px] pointer-events-none z-0"></div>

  <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center relative z-20 sm:w-fit ">
    {/* LEFT: Scaling & Skill Acquisition */}
    <div className="relative z-30 md:max-w-[105%] md:w-[650px] sm:w-fit">
      

      <h1 className="sm:w-auto text-5xl md:text-6xl lg:text-7xl font-black leading-[1.1] mb-6 text-zinc-950 tracking-tighter md:max-w-[105%] md:w-[650px]">
        Acquire The Skills
        <span className="text-transparent bg-clip-text bg-[#f59e0b]">
          &nbsp; To Scale With AI.
        </span>
      </h1>

      

      <div className="flex flex-col sm:flex-row gap-4 mt-10">
        <Link
          to="/signup"
          className={`${actionColorScheme} px-8 py-4 rounded-xl font-bold text-lg text-center transition-all shadow-xl shadow-zinc-200`}
        >
          Browse All Courses
        </Link>
           
      </div>
    </div>

    {/* RIGHT: Simple Picture */}
    <div className="relative w-full  h-[550px] md:h-[440px] lg:h-[550px] flex items-center justify-center lg:justify-end h-fit">
      {/* Here is the simple picture wrapper. 
         Aspect-square keeps it neat, max-w limits width on large screens.
         object-cover ensures the image fills the area.
      */}
      <div className="w-full h-fit max-w-[500px] md:max-w-[420px] lg:max-w-[500px] aspect-square rounded-3xl overflow-hidden shadow-2xl shadow-[#f59e0b] border-4 border-white z-10">
        <img
          src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=800&auto=format&fit=crop"
          alt="Business team collaborating on AI scaling strategy"
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  </div>
</section>
             



{/* ========================================
    2. HOW IT WORKS: The Scaling Protocol (Light Mode - High Contrast Hover)
    ======================================== */}
 {/* ========================================
                    2. HOW IT WORKS: The Scaling Protocol (Restored Sticky Layout)
                    ======================================== */}
                <section className="bg-zinc-950 py-24 md:py-32 relative">
                    <div className="absolute top-0 right-1/4 w-[600px] h-[300px] bg-amber-500/5 rounded-full blur-[120px] pointer-events-none"></div>

                    <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10 flex flex-col lg:flex-row gap-16 items-start">
                        
                        {/* LEFT COLUMN: Sticky */}
                        <div className="w-full lg:w-1/3 lg:sticky lg:top-8">
                            <div className="pt-4">
                               
                                <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tighter leading-tight">
                                    Your 3-Step <br className="hidden lg:block"/> Scaling Plan
                                </h2>
                                <p className="text-zinc-400 text-lg leading-relaxed mb-8 max-w-sm">
                                    We move you from fundamental concepts to deploying complex business automations.
                                </p>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: Steps Cards */}
                        <div className="w-full lg:w-2/3 flex flex-col gap-8">
                            {/* Step 1 */}
                            <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-8 md:p-12 overflow-hidden group hover:border-[#f59e0b] transition-all shadow-xl">
                                <div className="absolute -right-4 -bottom-10 text-[150px] font-black text-zinc-800/10 group-hover:text-[#f59e0b29] transition-colors leading-none">01</div>
                                <div className="relative z-10">
                                    <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8  transition-all">
                                        <GraduationCap className="w-8 h-8 text-[#f59e0b]" />
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Acquire Core Skills</h3>
                                    <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-lg font-medium">
                                        Master <strong>AI Fundamentals for Professionals</strong>. Understand the underlying logic of GPT and automated intelligence.
                                    </p>
                                </div>
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-[#f59e0b] group-hover:h-1/2 transition-all duration-500 rounded-r-full"></div>
                            </div>

                            {/* Step 2 */}
                            <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-8 md:p-12 overflow-hidden group hover:border-[#f59e0b] transition-all shadow-xl">
                                <div className="absolute -right-4 -bottom-10 text-[150px] font-black text-[f59e0b29] group-hover:text-[#f59e0b29] transition-colors leading-none">02</div>
                                <div className="relative z-10">
                                    <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8  transition-all">
                                        <Briefcase className="w-8 h-8 text-[#f59e0b]" />
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Strategic Execution</h3>
                                    <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-lg font-medium">
                                        Apply <strong>AI Operations Management</strong> and <strong>Market Analysis</strong> to find profit-holes in your business.
                                    </p>
                                </div>
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-[#f59e0b] group-hover:h-1/2 transition-all duration-500 rounded-r-full"></div>
                            </div>

                            {/* Step 3 */}
                            <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-8 md:p-12 overflow-hidden group hover:border-amber-500/30 transition-all shadow-xl">
                                <div className="absolute -right-4 -bottom-10 text-[150px] font-black text-zinc-800/10 group-hover:text-[#f59e0b29] transition-colors leading-none">03</div>
                                <div className="relative z-10">
                                    <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8  transition-all">
                                        <TrendingUp className="w-8 h-8 text-[#f59e0b]" />
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Scale Your Authority</h3>
                                    <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-lg font-medium">
                                        Use <strong>ChatGPT for Branding</strong> to scale your personal presence on social media and dominate your niche.
                                    </p>
                                </div>
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-[#f59e0b] group-hover:h-1/2 transition-all duration-500 rounded-r-full"></div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ========================================
                    3. ABOUT US: The Mission
                    ======================================== */}
                <section className="py-24 bg-white overflow-hidden border-t border-zinc-100">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
                        <div className="order-2 lg:order-1">
                            <span className="inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-4 py-1.5 text-xs font-bold text-zinc-600 mb-6 uppercase tracking-widest shadow-sm text-center">The Business Mission</span>
                            <h2 className="text-4xl md:text-5xl font-black text-zinc-900 mb-6 leading-tight tracking-tighter">
                                Bridging the Gap Between <br/> Complexity & Profit.
                            </h2>
                            <p className="text-lg text-zinc-600 mb-6 font-medium leading-relaxed">
                                NeuralAcademy was created for entrepreneurs who don't have time for theory. Every course in our Courses is a direct skill acquisition module meant for immediate business application.
                            </p>
                           
                        </div>
                        <div className="relative order-1 lg:order-2">
                            <div className="absolute -top-6 -right-6 w-64 h-64" style={{ backgroundImage: 'radial-gradient(#d4d4d8 2px, transparent 2px)', backgroundSize: '20px 20px' }}></div>
                            <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Leadership" className="relative z-10 rounded-3xl shadow-2xl shadow-[#f59e0b] w-full h-[450px] md:h-[360px] lg:h-[450px] object-cover border-4 border-zinc-100" />
                        </div>
                    </div>
                </section>


                {/* ========================================
                    Get started
                    ======================================== */}

<section className="bg-white py-24 md:py-32 border-y border-zinc-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        
        {/* Top Header */}
        <div className="mb-16 md:mb-24 text-center ">
          <div className="mx-auto inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-4 py-1.5 text-xs font-bold text-zinc-600 mb-6 uppercase tracking-widest shadow-sm text-center">
            Get started
          </div>
       
        </div>

        {/* Main Interactive Section */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-center">
          
          {/* Left Side: Dynamic Image Gallery */}
          <div className="w-full lg:w-1/2 relative h-[400px] md:h-[420px] lg:h-[650px] rounded-[2.5rem] overflow-hidden bg-zinc-100 shadow-2xl shadow-[#f59e0b]">
            {steps.map((step) => (
              <img
                key={step.id}
                src={step.image}
                alt={step.title}
                className={`absolute inset-0 w-full h-full object-cover transition-all duration-1000 ease-in-out ${
                  activeStep === step.id 
                    ? 'opacity-100 scale-100' 
                    : 'opacity-0 scale-105 pointer-events-none'
                }`}
              />
            ))}
            {/* Subtle inner shadow overlay */}
            <div className="absolute inset-0 border border-black/5 rounded-[2.5rem] z-10 pointer-events-none"></div>
          </div>

          {/* Right Side: Expandable Accordion List */}
          <div className="w-full lg:w-1/2 flex flex-col justify-center">
            {steps.map((step) => {
              const isActive = activeStep === step.id;

              return (
                <div 
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={`group cursor-pointer border-b border-zinc-200 py-8 transition-all duration-300 ${
                    isActive ? 'opacity-100' : 'opacity-50 hover:opacity-100'
                  }`}
                >
                  {/* Step Header (Always Visible) */}
                  <div className="flex items-center gap-6">
                    <span className={`text-4xl md:text-5xl font-black transition-colors duration-300 ${
                      isActive ? 'text-zinc-900' : 'text-zinc-300 group-hover:text-zinc-400'
                    }`}>
                      {step.num}
                    </span>
                    <h3 className="text-2xl md:text-3xl font-bold text-zinc-900 tracking-tight flex-1">
                      {step.title}
                    </h3>
                    
                    {/* Animated Arrow Icon */}
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 text-lg shadow-xl shadow-zinc-200 ${actionColorScheme} ${isActive ? '' : 'opacity-80'}`}
                    >
                      <ArrowRight className={`w-5 h-5`} />
                    </div>
                  </div>

                  {/* Expandable Description (Uses CSS Grid for smooth height transition) */}
                  <div 
                    className={`grid transition-all duration-500 ease-in-out ${
                      isActive ? 'grid-rows-[1fr] opacity-100 mt-6' : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden pl-14 md:pl-20 pr-4">
                      <p className="text-zinc-500 text-lg leading-relaxed font-medium">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </section>  

  
            </div>
            <Footer />
        </>
    );
};

export default AILearningHome;